import React, { useRef, type ReactNode, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Stack, Inline, xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useInfiniteScrollHandler } from '@atlassian/jira-native-issue-table/src/controllers/infinite-scroll-handler/index.tsx';
import { ScrollStateProvider } from '@atlassian/jira-native-issue-table/src/controllers/scroll-state/index.tsx';
import type { cardContainer_issueNavigator_issueResults$key as IssuesFragment } from '@atlassian/jira-relay/src/__generated__/cardContainer_issueNavigator_issueResults.graphql';
import type { cardContainer_issueNavigator_view$key as ViewFragment } from '@atlassian/jira-relay/src/__generated__/cardContainer_issueNavigator_view.graphql';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { views } from '../../../../common/constants.tsx';
import PageText from '../../../../common/ui/page-text/index.tsx';
import RefreshButton from '../../../../common/ui/refresh-button/index.tsx';
import { useInfiniteScroll } from '../../../../services/infinite-scroll/index.tsx';
import { LoadingManager } from './loading-manager/index.tsx';
import OrderDropdown from './order-dropdown/index.tsx';
import SortButton from './sort-button/index.tsx';

type Props = {
	issueResults: IssuesFragment;
	view: ViewFragment;
	children: ReactNode;
};

const infiniteScrollOptions = {
	threshold: 5 * 70, // Approximately 5 min-height cards tall
};

const CardContainer = ({ children, issueResults, view }: Props) => {
	const data = useFragment<IssuesFragment>(
		graphql`
			fragment cardContainer_issueNavigator_issueResults on JiraIssueConnection {
				edges {
					__typename
				}
				totalIssueSearchResultCount
				isCappingIssueSearchResult
			}
		`,
		issueResults,
	);

	const viewData = useFragment<ViewFragment>(
		graphql`
			fragment cardContainer_issueNavigator_view on JiraIssueSearchViewMetadata {
				fieldSets(
					first: $amountOfColumns
					filter: { fieldSetSelectedState: SELECTED }
					scope: $fieldSetsScope
				) {
					edges {
						node {
							fieldSetId
						}
					}
				}
			}
		`,
		view,
	);

	const fieldSetIds = useMemo(
		() => viewData?.fieldSets?.edges?.map((edge) => edge?.node?.fieldSetId).filter(Boolean) ?? [],
		[viewData?.fieldSets?.edges],
	);

	const infiniteScrollProps = useInfiniteScroll({
		fieldSetIds,
		options: infiniteScrollOptions,
		view: views.detail,
	});
	const { isLoadingPrevious, isLoadingNext } = infiniteScrollProps;

	const { onScroll, onLoadingThresholdCheck } = useInfiniteScrollHandler(infiniteScrollProps);

	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const totalIssues = data.totalIssueSearchResultCount ?? 0;

	return (
		<Stack
			space={isVisualRefreshEnabled() ? undefined : 'space.025'}
			grow="fill"
			xcss={isVisualRefreshEnabled() ? containerStyles : containerStylesOld}
			testId="issue-navigator.ui.issue-results.detail-view.card-container.styled-container"
		>
			<Inline
				space="space.050"
				xcss={isVisualRefreshEnabled() ? headerStyles : headerStylesOld}
				alignBlock={isVisualRefreshEnabled() ? 'center' : undefined}
			>
				<OrderDropdown />
				<SortButton />
				<RefreshButton />
			</Inline>
			<ScrollStateProvider scrollRef={scrollContainerRef}>
				<Stack grow="fill" xcss={loadingIndicatorContainerStyles}>
					<Box
						ref={scrollContainerRef}
						xcss={cardListStyles}
						onScroll={onScroll}
						testId="issue-navigator.ui.issue-results.detail-view.card-container.scroll-container"
					>
						{children}
					</Box>
					<LoadingManager
						isLoadingNext={isLoadingNext}
						isLoadingPrevious={isLoadingPrevious}
						onLoadingThresholdCheck={onLoadingThresholdCheck}
					/>
				</Stack>
			</ScrollStateProvider>
			{totalIssues != null && totalIssues > 0 && (
				<Inline
					alignBlock="center"
					alignInline="center"
					xcss={footerStyles}
					testId="issue-navigator.ui.issue-results.detail-view.card-container.total-count"
				>
					<PageText
						startIssueRange={-1}
						endIssueRange={-1}
						total={totalIssues}
						countExceedsMaxResults={!!data.isCappingIssueSearchResult}
						loadedIssuesCount={data.edges?.length ?? 0}
						isInfiniteScrollEnabled
					/>
				</Inline>
			)}
		</Stack>
	);
};

export default CardContainer;

const containerStylesOld = xcss({
	borderRadius: token('radius.small'),
	backgroundColor: 'color.background.neutral',
	boxSizing: 'content-box',
	padding: 'space.025',
	marginBottom: 'space.100',
	marginRight: 'space.200',
	minWidth: '256px',
	maxWidth: '256px',
});

const containerStyles = xcss({
	borderRadius: token('radius.small'),
	backgroundColor: 'elevation.surface.sunken',
	boxSizing: 'content-box',
	paddingInline: 'space.025',
	marginBottom: 'space.100',
	marginRight: 'space.200',
	minWidth: '256px',
	maxWidth: '256px',
});

const headerStylesOld = xcss({
	flexGrow: 1,
	flexShrink: 0,
	height: '32px', // gridSize * 4
});

const headerStyles = xcss({
	flexGrow: 1,
	flexShrink: 0,
	height: '40px',
	marginBottom: 'space.negative.025', // Cards require small margin to show box shadow. This negative margin recenters the header.
	paddingInline: 'space.025',
});

const loadingIndicatorContainerStyles = xcss({
	flexGrow: '1',
	flexShrink: '1',
	flexBasis: '100vh',
	overflow: 'hidden',
	position: 'relative', // Required in order to contain loading indicator
});

const cardListStyles = xcss({
	flexGrow: '1',
	overflow: 'auto',
});

const footerStyles = xcss({
	color: 'color.text.subtlest',
	paddingBlock: 'space.050',
	textAlign: 'center',
});
